// Shared Transaction History Component
// Displays DataTrace transaction history with status checking and report retrieval
import React, { useState } from 'react';
import './TransactionHistory.css';

function TransactionHistory({
  transactions = [],
  loading = false,
  pollingTransactions = {},
  onCheckStatus,
  onGetReport,
  onSaveReportAndDocuments,
  savingReport = {},
  showHistory = false,
  onToggleHistory
}) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, address, status

  if (!showHistory && onToggleHistory) {
    return (
      <div className="history-panel">
        <button
          className="history-toggle-btn"
          onClick={onToggleHistory}
        >
          📜 Transaction History ({transactions.length})
          <span className="toggle-icon">▶</span>
        </button>
      </div>
    );
  }

  // Helper to determine if a transaction is from a synchronous product
  const isSyncTransaction = (txn) => {
    // Synchronous products: property_data (includes instant_recordable_legal and property_detail)
    // Asynchronous products: tax, tiqsa, and others
    const syncProducts = ['property_data'];
    return syncProducts.includes(txn.productCode);
  };

  // Helper to determine if a transaction is completed
  const isCompleted = (txn) => {
    const status = (txn.status || '').toLowerCase().trim();
    // Check if status contains 'completed', 'success', 'available', or 'fulfilled'
    // This handles: COMPLETED, COMPLETED_WITH_ERRORS, SUCCESS, AVAILABLE, FULFILLED, etc.
    return status.includes('completed') || status.includes('success') ||
           status.includes('available') || status.includes('fulfilled');
  };

  // Format JSON for display
  const formatJSON = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  // Get unique statuses and products for filter options
  const uniqueStatuses = [...new Set(transactions.map(t => t.status || 'Pending'))].sort();
  const uniqueProducts = [...new Set(transactions.map(t => t.productCode))].sort();

  // Apply filters and search
  let filteredTransactions = transactions.filter(txn => {
    // Search filter - search in address, transaction ID, client order number, and product
    const matchesSearch = searchQuery === '' ||
      (txn.address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (txn.transactionId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (txn.clientOrderNumber || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (txn.productCode || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (txn.productType || '').toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter
    const matchesStatus = statusFilter === 'all' || (txn.status || 'Pending') === statusFilter;

    // Product filter
    const matchesProduct = productFilter === 'all' || txn.productCode === productFilter;

    return matchesSearch && matchesStatus && matchesProduct;
  });

  // Apply sorting
  filteredTransactions = [...filteredTransactions].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.timestamp) - new Date(a.timestamp);
      case 'oldest':
        return new Date(a.timestamp) - new Date(b.timestamp);
      case 'address':
        return (a.address || '').localeCompare(b.address || '');
      case 'status':
        return (a.status || 'Pending').localeCompare(b.status || 'Pending');
      default:
        return 0;
    }
  });

  return (
    <div className="history-panel">
      {onToggleHistory && (
        <button
          className="history-toggle-btn"
          onClick={onToggleHistory}
        >
          📜 Transaction History ({transactions.length})
          <span className="toggle-icon">▼</span>
        </button>
      )}

      <div className="history-content">
        {/* Search and Filter Controls */}
        {transactions.length > 0 && (
          <div className="history-controls">
            <div className="history-search">
              <input
                type="text"
                placeholder="Search by address, transaction ID, client order number, or product..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="history-search-input"
              />
              {searchQuery && (
                <button
                  className="history-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="history-filters">
              <div className="history-filter-group">
                <label>Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="history-filter-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="address">Address (A-Z)</option>
                  <option value="status">Status (A-Z)</option>
                </select>
              </div>

              <div className="history-filter-group">
                <label>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="history-filter-select"
                >
                  <option value="all">All Statuses</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="history-filter-group">
                <label>Product:</label>
                <select
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.target.value)}
                  className="history-filter-select"
                >
                  <option value="all">All Products</option>
                  {uniqueProducts.map(product => (
                    <option key={product} value={product}>{product}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="history-stats">
              Showing {filteredTransactions.length} of {transactions.length} transactions
            </div>
          </div>
        )}

        {loading ? (
          <div className="history-loading">Loading history...</div>
        ) : transactions.length === 0 ? (
          <div className="history-empty">No transaction history yet</div>
        ) : filteredTransactions.length === 0 ? (
          <div className="history-empty">
            No transactions match your filters.
            <button
              className="history-clear-filters"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setProductFilter('all');
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="history-list">
            {filteredTransactions.map((txn) => {
              const isSync = isSyncTransaction(txn);
              const hasResponseData = txn.responseData || txn.statusResponse;

              return (
                <div key={txn.transactionId} className="history-item">
                  <div className="history-item-header">
                    <div className="history-item-info">
                      <span className={`history-status-badge ${txn.status || 'pending'}`}>
                        {txn.status || 'Pending'}
                      </span>
                      <span className="history-timestamp">
                        {new Date(txn.timestamp).toLocaleString()}
                      </span>
                      {isSync && <span className="sync-badge" title="Synchronous API call">⚡ Sync</span>}
                    </div>
                    <div className="history-item-actions">
                      {/* For synchronous transactions, show View Request/Response button */}
                      {isSync && hasResponseData && (
                        <button
                          className="history-view-btn"
                          onClick={() => setSelectedTransaction(txn)}
                          title="View request and response payloads"
                        >
                          📄 View Details
                        </button>
                      )}

                      {/* For asynchronous transactions, show Check Status button */}
                      {!isSync && onCheckStatus && (
                        <button
                          className="history-check-btn"
                          onClick={() => onCheckStatus(txn.transactionId)}
                          disabled={pollingTransactions[txn.transactionId] === 'loading'}
                          title="Check transaction status"
                        >
                          {pollingTransactions[txn.transactionId] === 'loading'
                            ? '⏳'
                            : '🔄'}
                        </button>
                      )}

                      {/* Show Get Report button for completed ASYNC transactions only - Sync products already have response data */}
                      {onGetReport && !isSync && isCompleted(txn) && (
                        <button
                          className="history-report-btn"
                          onClick={() => onGetReport(txn.transactionId, txn.productCode)}
                          title="Download report"
                        >
                          📄 Get Report
                        </button>
                      )}

                      {/* Show Save Report & Documents button for completed ASYNC transactions */}
                      {onSaveReportAndDocuments && !isSync && isCompleted(txn) && (
                        <button
                          className="history-save-btn"
                          onClick={() => onSaveReportAndDocuments(
                            txn.transactionId,
                            txn.productCode,
                            txn.searchParams || {
                              address: txn.address || '',
                              city: txn.city || '',
                              state: txn.state || '',
                              zip: txn.zip || '',
                              county: txn.county || '',
                              countyFips: txn.countyFips || '',
                              apn: txn.apn || ''
                            }
                          )}
                          disabled={savingReport[txn.transactionId]}
                          title="Save report and download all associated documents to server"
                        >
                          {savingReport[txn.transactionId] ? '⏳ Saving...' : '💾 Save Report & Docs'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="history-item-details">
                    {txn.clientOrderNumber && (
                      <div><strong>Client Order Number:</strong> {txn.clientOrderNumber}</div>
                    )}
                    <div><strong>Address:</strong> {txn.address}</div>
                    <div><strong>Product:</strong> {txn.productCode} / {txn.productType}</div>
                    <div><strong>Transaction ID:</strong> {txn.transactionId}</div>
                    {txn.confirmationNumber && (
                      <div><strong>Confirmation:</strong> {txn.confirmationNumber}</div>
                    )}
                    {txn.lastChecked && (
                      <div><strong>Last Checked:</strong> {new Date(txn.lastChecked).toLocaleString()}</div>
                    )}
                  </div>
                  {txn.statusResponse && !isSync && (
                    <details className="history-status-details">
                      <summary>View Latest Status</summary>
                      <pre>{formatJSON(txn.statusResponse)}</pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request/Response Modal for Synchronous Transactions */}
      {selectedTransaction && (
        <div className="transaction-modal-overlay" onClick={() => setSelectedTransaction(null)}>
          <div className="transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="transaction-modal-header">
              <h3>Transaction Details</h3>
              <button className="modal-close-btn" onClick={() => setSelectedTransaction(null)}>
                ✕
              </button>
            </div>
            <div className="transaction-modal-content">
              <div className="transaction-modal-section">
                <h4>Transaction Info</h4>
                <div className="transaction-info-grid">
                  {selectedTransaction.clientOrderNumber && (
                    <div><strong>Client Order Number:</strong> {selectedTransaction.clientOrderNumber}</div>
                  )}
                  <div><strong>Transaction ID:</strong> {selectedTransaction.transactionId}</div>
                  <div><strong>Status:</strong> {selectedTransaction.status}</div>
                  <div><strong>Address:</strong> {selectedTransaction.address}</div>
                  <div><strong>Product:</strong> {selectedTransaction.productCode} / {selectedTransaction.productType}</div>
                  <div><strong>Confirmation:</strong> {selectedTransaction.confirmationNumber || 'N/A'}</div>
                  <div><strong>Timestamp:</strong> {new Date(selectedTransaction.timestamp).toLocaleString()}</div>
                </div>
              </div>

              <div className="transaction-modal-section">
                <h4>Request Payload</h4>
                <pre className="transaction-modal-code">
                  {formatJSON(selectedTransaction.requestPayload)}
                </pre>
              </div>

              {selectedTransaction.responseData && (
                <div className="transaction-modal-section">
                  <h4>Response Data</h4>
                  <pre className="transaction-modal-code">
                    {formatJSON(selectedTransaction.responseData)}
                  </pre>
                </div>
              )}

              {selectedTransaction.statusResponse && (
                <div className="transaction-modal-section">
                  <h4>Status Response</h4>
                  <pre className="transaction-modal-code">
                    {formatJSON(selectedTransaction.statusResponse)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionHistory;
